/**
 * The right-hand column of the trainer: what this node is, and what it just said.
 *
 * Everything here is read off the chart node and the server's verdict. Nothing is
 * inferred about poker that the numbers do not already state — the WHY panel in
 * particular describes the SPOT (how wide the actor plays, what the call is being
 * laid, how many seats are behind) rather than explaining the solver's reasoning,
 * which nobody has. A trainer that narrates a rationale it does not possess is the
 * thing this product exists not to be.
 */
import { useEffect, useRef } from "react";
import type { ChartNode, Grade } from "../lib/api";
import { seatsFor } from "../lib/table";
import { breakevenFold, mixFor, potOdds, rangeWidth, wholePercents } from "../lib/range";

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
const bb = (v: number) => (Math.abs(v % 1) < 1e-9 ? v.toFixed(0) : v.toFixed(2));

/**
 * What a jam can actually WIN, which is not the pot.
 *
 * `emit-multiway` builds an opening node's pot from every seat's posted chips, the
 * hero's own blind and ante included. A jam does not win back money that is already
 * yours, so counting it made the breakeven read four points softer than it is and the
 * sentence claim half again as much money as was on offer — at 10 bb heads-up, "risks
 * 9.5 to win 1.5" for a pot that really offers 1.
 *
 * The caller's side is different and is left alone: a seat facing a jam has already
 * put its blind in and cannot take it back, so its own posting genuinely is dead money
 * and belongs in the pot-odds denominator.
 */
function winnable(node: ChartNode): number {
  const hero = seatsFor(node).seats.find((s) => s.state === "hero");
  return Math.max(0, node.potBb - (hero?.posted ?? 0));
}

/* ------------------------------------------------------------------ tiles --- */

export function NodeReadout({ node, dealtCode, tableSize, stackDepth, revealed }: {
  node: ChartNode; dealtCode: string | null; tableSize: number; stackDepth: number;
  /**
   * Range width is the answer in one number.
   *
   * "The cutoff plays 27.3% here" tells a player roughly where the cutoff of the range
   * falls, which is most of what the drill is asking them for — so it is covered until
   * the hand is graded, exactly like the grid. Everything else on this panel is
   * readable off the felt already: the seat, the stacks, the pot, and the price of the
   * call, which is arithmetic on numbers printed in front of them.
   */
  revealed: boolean;
}) {
  const { seats } = seatsFor(node);
  const behind = seats.filter((s) => s.state === "waiting").length;
  const call = node.actions.find((a) => a.id === "call");
  const jam = node.actions.find((a) => a.id === "shove");
  const width = rangeWidth(node.strategy, node.actions);
  const odds = call?.size !== undefined ? potOdds(node.potBb, call.size) : null;

  return (
    <ul className="tiles">
      <Tile label="Your hand" value={dealtCode ?? "—"} />
      <Tile label="Seat" value={node.actor}
            hint={`${behind} ${behind === 1 ? "seat" : "seats"} behind`} />
      <Tile label="Range width"
            value={!revealed ? "·  ·  ·" : width === null ? "—" : pct(width)}
            hint={!revealed ? "after the hand" : width === null ? "not measured" : "of hands dealt"} />
      {/*
        * Three cases, not two. A blind that is already all-in from its own posting
        * owes nothing to call — `emit-multiway` gives it a size of zero — and the
        * tile used to fall through to the jam branch and read "Risking — to win
        * 12.50", which is the wrong label for a node where calling is free.
        */}
      {odds !== null ? (
        <Tile label="Pot odds" value={pct(odds)}
              hint={`${bb(call!.size!)} to win ${bb(node.potBb)}`} />
      ) : call ? (
        <Tile label="To call" value="0 bb" hint="already all in from the blind" />
      ) : (
        <Tile label="Risking"
              value={jam?.size !== undefined ? `${bb(jam.size)} bb` : "—"}
              hint={`to win ${bb(winnable(node))} bb`} />
      )}
      <Tile label="Table" value={tableSize === 2 ? "HU" : `${tableSize}-max`} />
      <Tile label="Stack" value={`${stackDepth} bb`} />
    </ul>
  );
}

export function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <li className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value num">{value}</span>
      {hint && <span className="tile-hint">{hint}</span>}
    </li>
  );
}

/* --------------------------------------------------------------- mix bars --- */

/**
 * The equilibrium mix for the hand in front of you, as bars.
 *
 * The verdict table already prints the same percentages. The bars are for the case
 * the table reads badly: a 63/37 mix and a 97/3 mix are two rows of digits and two
 * obviously different pictures, and "how mixed is this" is the thing a player is
 * actually trying to internalise.
 */
export function MixBars({ node, dealtCode, best }: {
  node: ChartNode; dealtCode: string; best: string | null;
}) {
  const mix = mixFor(node.strategy, dealtCode, node.actions.length);
  if (!mix) {
    return <p className="muted small">This chart does not cover {dealtCode}.</p>;
  }
  const shown = wholePercents(mix);
  return (
    <ul className="mixbars">
      {node.actions.map((a, i) => {
        const p = shown[i] ?? 0;
        return (
          <li key={a.id} className={a.id === best ? "mix-best" : ""}>
            <span className="mix-name">{a.label}</span>
            <span className="mix-track">
              <span className="mix-fill" style={{ width: `${p}%` }} />
            </span>
            <span className="mix-num num">{p}%</span>
          </li>
        );
      })}
    </ul>
  );
}

/* --------------------------------------------------------------------- why --- */

/**
 * Why this spot is shaped the way it is.
 *
 * Three sentences, each of which is a restatement of a number on this screen rather
 * than a claim about the game. That restraint is the point: the honest thing a chart
 * can tell you is what it does, and "the cutoff jams 34% of hands here, which is
 * wider than it would be with four seats behind" is a fact about the chart. "You
 * should jam because you have fold equity" is a story, and the pipeline never
 * computed it.
 */
export function WhyPanel({ node, dealtCode, grade, revealed }: {
  node: ChartNode; dealtCode: string | null; grade: Grade | null;
  /**
   * The first and last paragraphs describe the RANGE, which is the answer. The middle
   * one prices the call from the pot and the bet — both printed on the felt — so it is
   * the half of this panel that helps somebody decide rather than telling them what
   * they were about to be asked.
   */
  revealed: boolean;
}) {
  const { seats } = seatsFor(node);
  const behind = seats.filter((s) => s.state === "waiting").length;
  const width = rangeWidth(node.strategy, node.actions);
  const call = node.actions.find((a) => a.id === "call");
  const jam = node.actions.find((a) => a.id === "shove");
  const odds = call?.size !== undefined ? potOdds(node.potBb, call.size) : null;
  const win = winnable(node);
  const be = jam?.size !== undefined ? breakevenFold(jam.size, win) : null;
  const mix = dealtCode ? mixFor(node.strategy, dealtCode, node.actions.length) : null;
  const foldI = node.actions.findIndex((a) => a.id === "fold");
  const playFreq = mix && foldI >= 0 ? 1 - (mix[foldI] ?? 0) : null;

  return (
    <div className="why">
      {revealed ? (
      <p>
        {width === null ? (
          <>This chart carries no frequencies for the {node.actor}, so its width cannot be stated.</>
        ) : (
          <>
            At equilibrium the {node.actor} plays{" "}
            <span className="num">{pct(width)}</span> of the hands it is dealt here, with{" "}
            <span className="num">{behind}</span> {behind === 1 ? "seat" : "seats"} still to act behind it.
          </>
        )}
      </p>
      ) : null}

      {odds !== null ? (
        <p>
          Calling costs <span className="num">{bb(call!.size!)}</span> bb into a pot of{" "}
          <span className="num">{bb(node.potBb)}</span>, so the call is being laid{" "}
          <span className="num">{pct(odds)}</span> — that is the share of the final pot
          being risked, not the equity needed, which the solve accounts for separately.
        </p>
      ) : be !== null ? (
        <p>
          The jam risks <span className="num">{bb(jam!.size!)}</span> bb to win{" "}
          <span className="num">{bb(win)}</span> — the pot less your own blind and
          ante, which were never at risk — so it breaks even on folds alone at{" "}
          <span className="num">{pct(be)}</span>. That is a floor rather than the bar: the
          jam also wins some of the hands that call it.
        </p>
      ) : null}

      {revealed && dealtCode && playFreq !== null && (
        <p>
          {playFreq >= 0.995 ? (
            <><span className="num">{dealtCode}</span> is inside the pure part of that range —
            it takes the same action every time.</>
          ) : playFreq <= 0.005 ? (
            <><span className="num">{dealtCode}</span> is outside it. Not close to the edge —
            the chart never plays it here.</>
          ) : (
            <><span className="num">{dealtCode}</span> sits on the edge, played{" "}
            <span className="num">{Math.round(playFreq * 100)}%</span> of the time. Hands
            like this are where a range is decided and where the cost of guessing is
            smallest.</>
          )}
          {grade?.indifferent ? " This solve cannot separate the two actions for it." : ""}
        </p>
      )}

      {!revealed && (
        <p className="why-wait">
          What the chart plays here is covered until the hand is graded — reading it
          first would make this a reading exercise rather than a decision.
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- hand history --- */

export interface HistoryRow {
  code: string;
  action: string;
  actionLabel: string;
  bestLabel: string | null;
  cost: number;
  correct: boolean;
  indifferent: boolean;
}

/**
 * The last hands, newest first.
 *
 * A session stat says you are at 82%. It does not say that all four misses were
 * offsuit aces from the small blind, which is the only version of that number anybody
 * can act on. Capped at twenty because this is a scan, not a record — the leak report
 * is where a record lives.
 */
export const HISTORY_MAX = 20;

export function HandHistory({ rows }: { rows: HistoryRow[] }) {
  const listRef = useRef<HTMLOListElement>(null);
  /**
   * A new row at the top is worth nothing if the list is scrolled somewhere else.
   *
   * Keyed on the newest ROW, not the length. The list is capped at twenty, so once it
   * is full the length is a constant and the effect stopped firing — the panel looked
   * like it had stopped updating from hand twenty-one onwards, which is precisely when
   * somebody has drilled long enough to be reading it.
   */
  useEffect(() => { listRef.current?.scrollTo({ top: 0 }); }, [rows[0]]);

  if (rows.length === 0) {
    return <p className="muted small">Hands you have played appear here.</p>;
  }
  return (
    <ol className="th" ref={listRef}>
      {rows.map((r, i) => (
        <li key={rows.length - i} className={r.indifferent ? "th-even" : r.correct ? "th-ok" : "th-bad"}>
          <span className="th-code num">{r.code}</span>
          <span className="th-act">{r.actionLabel}</span>
          <span className="th-verdict num">
            {r.indifferent ? "either" : r.correct ? "✓" : `−${(r.cost * 100).toFixed(1)}%`}
          </span>
        </li>
      ))}
    </ol>
  );
}
